import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useMutation } from '@apollo/client';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarIcon,
  CheckCircleIcon,
  CheckIcon,
  LogoutIcon,
  RefreshIcon,
  BackspaceIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/outline';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import {
  GENERATE_INSTALL_LINK,
  NOTIFY_SETUP_GUIDE_COMPLETED,
  SET_STORE_PLAN,
  SETUP_STRIPE_CONNECTION,
  UPDATE_STORE_ONBOARDING,
  UPDATE_USER_INFO,
} from 'gql/mutations';

import Step1 from './Step1';
import Step2, { PLATFORM } from './Step2';
import Step3, { OFFERINGS } from './Step3';
import Step4 from './Step4';
import Step5 from './Step5';
import OptionMenu from 'components/OptionMenu';
import { useSignOut } from '@nhost/react';
import { SelectStoreModal } from 'components/SelectStoreModal';
import ThankYou from './ThankYou';

export enum BILLING_PROVIDER {
  SHOPIFY_BILLING = 'SHOPIFY_BILLING',
  STRIPE_BILLING = 'STRIPE_BILLING',
}

export interface IOnboardingForm {
  platform: PLATFORM;
  platformOther?: string;
  shopifyUrl: string;
  useCustomApp: boolean;
  customAppInstallLink?: string;
  offering?: OFFERINGS;
  billingProvider?: BILLING_PROVIDER;
  stripeBillingEmail?: string;
  offeringNotes: '';
  consent: boolean;
  enableCustomAppFlow?: boolean;
  completed?: boolean;
}

const totalSteps = 5;

const SetupGuideFlow = () => {
  const { signOut } = useSignOut(true);
  const [isStoreModalOpen, setIsStoreModalOpen] = useState<boolean>(false);

  const { user } = useUser();
  const { isAppLoaded, storeId, userStores, updateUserStore, storeProperties } =
    useStore();

  const [isOpen, setIsOpen] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<IOnboardingForm>({
    platform: PLATFORM.SHOPIFY,
    platformOther: '',
    shopifyUrl: '',
    useCustomApp: false,
    offering: undefined,
    billingProvider: undefined,
    offeringNotes: '',
    consent: false,
  });

  const [storeConnected, setStoreConnected] = useState(false);
  const [billingConnected, setBillingConnected] = useState(false);

  const [installLink, setInstallLink] = useState('');
  const [confirmedStoreUrl, setConfirmedUrl] = useState(false);
  const [
    generateInstallLinkMutation,
    { loading: generateInstallLinkLoading, error: generateInstallLinkError },
  ] = useMutation(GENERATE_INSTALL_LINK, {
    onCompleted: (data) => {
      setInstallLink(data?.generateInstallLink);
      setConfirmedUrl(true);
    },
  });

  const [setStorePlan] = useMutation(SET_STORE_PLAN, {
    onCompleted: (data) => {
      window.location.href = data?.setStorePlan?.chargeUrl;
    },
    onError: (error) => {},
  });

  const [setupStripeConnection] = useMutation(SETUP_STRIPE_CONNECTION, {
    onCompleted: (data) => {
      window.location.href = data.setupStripeConnection;
    },
  });

  const [notifySetupGuideCompleted] = useMutation(NOTIFY_SETUP_GUIDE_COMPLETED);

  const [updateStoreOnboarding] = useMutation(UPDATE_STORE_ONBOARDING);
  const [updateUserOnboarding] = useMutation(UPDATE_USER_INFO);

  const addingNewStoreProgressFlag = useMemo(() => {
    console.log(`[Current Store Selected]: ${storeId}`);
    return localStorage.getItem('new-store-adding-progress');
  }, [storeId]);

  const removeNewStoreProgressFlag = () => {
    localStorage.removeItem('new-store-adding-progress');
  };

  const initWizard = useRef(
    (storeId: any, storeProperties: any = {}, user: any = {}) => {
      const onboarding = {
        ...(user?.metadata?.onboarding || {}),
        ...(storeProperties?.onboarding || {}),
      };
      const hasStoreConnected = storeId && !storeProperties?.uninstalledAt;
      const isShopifyBillingConnected =
        !!storeProperties?.plan?.chargeConfirmed;
      const isStripeBillingConnected = !!storeProperties?.billing;

      const hasCustomAppFlow =
        user?.metadata?.enableCustomAppFlow ||
        storeProperties?.enableCustomAppFlow;

      const step = onboarding?.currentStep || 1;

      const hasBillingConnected =
        isStripeBillingConnected || isShopifyBillingConnected;

      if (hasStoreConnected) {
        setStoreConnected(true);
      }
      if (hasBillingConnected) {
        setBillingConnected(true);
      }

      setFormData((formData) => ({
        ...formData,
        ...(storeProperties?.onboarding || {}),
        shopifyUrl:
          storeProperties?.onboarding?.onboarding ||
          formData.shopifyUrl ||
          storeProperties?.store ||
          '',
        useCustomApp: hasCustomAppFlow ?? formData.useCustomApp,
        enableCustomAppFlow: hasCustomAppFlow ?? false,
      }));
      setCurrentStep(step);

      // Based on onboarding data 'completed', wizard is shown
      if (!(onboarding && onboarding.completed)) {
        setIsOpen(true);
        return;
      }

      if (!hasStoreConnected) {
        setCurrentStep(step < 2 ? step : 2);
        setIsOpen(true);
        return;
      }

      if (!hasBillingConnected) {
        setCurrentStep(step < 4 ? step : 4);
        setIsOpen(true);
        return;
      }
    },
  );

  useEffect(() => {
    if (addingNewStoreProgressFlag) {
      return setIsOpen(true);
    }
    if (!isAppLoaded) {
      if (!storeId && userStores?.length) {
        setIsOpen(true);
        setIsStoreModalOpen(true);
      } else {
        setIsStoreModalOpen(false);
        setIsOpen(false);
      }
      return;
    }
    initWizard.current(storeId, storeProperties, user);
  }, [
    isAppLoaded,
    storeId,
    storeProperties,
    user,
    userStores,
    addingNewStoreProgressFlag,
  ]);

  const saveOnboarding = useCallback(
    (payload = {}) => {
      const onboarding = {
        ...(storeProperties?.onboarding || {}),
        ...formData,
        ...(payload || {}),
      };
      if (storeId) {
        updateStoreOnboarding({
          variables: {
            storeId,
            onboarding,
          },
        });
      } else {
        updateUserOnboarding({
          variables: {
            id: user?.id,
            userObj: { metadata: { ...(user?.metadata || {}), onboarding } },
          },
        });
      }
    },
    [
      formData,
      storeId,
      user,
      storeProperties,
      updateStoreOnboarding,
      updateUserOnboarding,
    ],
  );

  const handleNext = () => {
    let step = currentStep;
    if (currentStep === 2 && formData.platform === PLATFORM.OTHER) {
      step = 5;
      return setCurrentStep(5);
    }

    if (currentStep < totalSteps) {
      step += 1;
      setCurrentStep(step);
    }
    saveOnboarding({ ...formData, currentStep: step });
  };

  const confirmDomain = useCallback(() => {
    if (formData.shopifyUrl) {
      generateInstallLinkMutation({
        variables: {
          store: formData.shopifyUrl,
        },
      });
      return;
    }
  }, [formData.shopifyUrl, generateInstallLinkMutation]);

  const editConfirmedDomain = useCallback(() => {
    setConfirmedUrl(false);
    setInstallLink('');
  }, []);

  const install = useCallback(() => {
    if (formData.useCustomApp && formData.customAppInstallLink) {
      saveOnboarding({ customAppInstallLink: formData.customAppInstallLink });
      window.location.href = formData.customAppInstallLink;
      return;
    }

    if (!installLink) {
      generateInstallLinkMutation({
        variables: {
          store: formData.shopifyUrl,
        },
      });
      return;
    }
    window.location.href = installLink;
  }, [
    installLink,
    formData.shopifyUrl,
    formData.useCustomApp,
    formData.customAppInstallLink,
    generateInstallLinkMutation,
    saveOnboarding,
  ]);

  const handlePrev = () => {
    if (currentStep > 1) {
      const step = currentStep - 1;
      setCurrentStep(step);
      saveOnboarding({ ...formData, currentStep: step });
    }
  };

  const handlePlatform = useCallback((platform: PLATFORM) => {
    setFormData((formData) => ({ ...formData, platform }));
  }, []);

  const handleOffering = useCallback((offering: OFFERINGS) => {
    setFormData((formData) => ({ ...formData, offering }));
  }, []);

  const handleShopifyUrl = useCallback(({ target: { value: domain } }) => {
    setFormData((formData) => ({ ...formData, shopifyUrl: domain }));
    setInstallLink('');
  }, []);

  const handleChangeOfferingNotes = useCallback(
    ({ target: { value: offeringNotes } }) => {
      setFormData((formData) => ({ ...formData, offeringNotes }));
    },
    [],
  );

  const handleBillingProvider = useCallback(
    (billingProvider: BILLING_PROVIDER) => {
      setFormData((formData) => ({ ...formData, billingProvider }));
    },
    [],
  );

  const handleStripeBillingEmail = useCallback(
    ({ target: { value: stripeBillingEmail } }) => {
      setFormData((formData) => ({ ...formData, stripeBillingEmail }));
      setInstallLink('');
    },
    [],
  );

  const handlePlatformOther = useCallback(
    (e: { target: { value: string } }) => {
      setFormData((formData) => ({
        ...formData,
        platformOther: e.target.value,
      }));
    },
    [],
  );

  const handleCustomAppInstallLink = useCallback(
    (e: { target: { value: string } }) => {
      setFormData((formData) => ({
        ...formData,
        customAppInstallLink: e.target.value,
      }));
    },
    [],
  );

  const onChangeUseCustomApp = useCallback(
    (e: { target: { checked: boolean } }) => {
      setFormData((formData) => ({
        ...formData,
        useCustomApp: e.target.checked,
      }));
    },
    [],
  );

  const onHandleConsent = useCallback((e: { target: { checked: boolean } }) => {
    setFormData((formData) => ({
      ...formData,
      consent: e.target.checked,
    }));
    setConsentError(!e.target.checked);
  }, []);
  const canProceed = () => {
    switch (currentStep) {
      case 2:
        return formData.platform && (formData.platform || formData.shopifyUrl);
      case 3:
        return formData.offering;
      case 4:
        return formData.billingProvider && billingConnected;
      default:
        return true;
    }
  };

  const StepIndicator = useMemo(
    () => (
      <div className="flex items-center justify-center mb-8">
        {[1, 2, 3, 4, 5].map((step) => (
          <div key={step} className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                step <= currentStep
                  ? 'bg-[#2563eb] text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {step < currentStep ? (
                <CheckCircleIcon className="w-16 h-16" />
              ) : (
                step
              )}
            </div>
            {step < 5 && (
              <div
                className={`w-16 h-1 mx-2 ${
                  step < currentStep ? 'bg-[#2563eb]' : 'bg-gray-200'
                }`}
              />
            )}
          </div>
        ))}
      </div>
    ),
    [currentStep],
  );

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-[10]">
              <h1 className="text-2xl font-bold text-gray-900">
                🚀 Launch in 5 Quick Steps
              </h1>
              <div>
                <OptionMenu
                  options={[
                    userStores && userStores.length > 1
                      ? {
                          label: 'Switch Stores',
                          onClick: () => {
                            setIsStoreModalOpen(true);
                          },
                          icon: <RefreshIcon className="h-5 w-5 mr-2" />,
                        }
                      : null,
                    {
                      label: 'Help',
                      onClick: () => {
                        window.open(
                          'https://calendly.com/shipaid-support/expert-call',
                          '_new',
                        );
                      },
                      icon: <QuestionMarkCircleIcon className="h-5 w-5 mr-2" />,
                    },
                    {
                      label: 'Logout',
                      onClick: () => signOut(true),
                      icon: <LogoutIcon className="h-5 w-5 mr-2" />,
                    },
                    addingNewStoreProgressFlag ||
                    (userStores && userStores.length)
                      ? {
                          label: 'Close',
                          onClick: () => {
                            removeNewStoreProgressFlag();

                            if (
                              updateUserStore &&
                              userStores &&
                              userStores.length
                            ) {
                              updateUserStore(userStores[0]);
                            }

                            setIsOpen(false);
                          },
                          icon: <BackspaceIcon className="h-5 w-5 mr-2" />,
                        }
                      : null,
                  ]}
                />
              </div>
            </div>

            <div className="p-6">
              {!showThankYou && StepIndicator}

              <div className="min-h-[400px]">
                {!showThankYou ? (
                  <>
                    {currentStep === 1 && <Step1 />}
                    {currentStep === 2 && (
                      <Step2
                        {...{
                          storeConnected,
                          formData: {
                            platform: formData.platform,
                            shopifyUrl: formData.shopifyUrl,
                            customAppInstallLink: formData.customAppInstallLink,
                            platformOther: formData.platformOther,
                            useCustomApp: formData.useCustomApp,
                            enableCustomAppFlow: formData.enableCustomAppFlow,
                          },
                          handlePlatform,
                          handleShopifyUrl,
                          handlePlatformOther,
                          onChangeUseCustomApp,
                          handleCustomAppInstallLink,
                          confirmDomain,
                          editConfirmedDomain,
                          generateInstallLinkError,
                          generateInstallLinkLoading,
                          confirmedStoreUrl,
                        }}
                      />
                    )}
                    {currentStep === 3 && (
                      <Step3
                        {...{
                          formData: {
                            platform: formData.platform,
                            offering: formData.offering,
                            offeringNotes: formData.offeringNotes,
                            useCustomApp: formData.useCustomApp,
                          },
                          handleOffering,
                          handleChangeOfferingNotes,
                        }}
                      />
                    )}
                    {currentStep === 4 && (
                      <Step4
                        {...{
                          formData: {
                            billingProvider: formData.billingProvider,
                            useCustomApp: formData.useCustomApp,
                            stripeBillingEmail: formData.stripeBillingEmail,
                            shopifyUrl: formData.shopifyUrl,
                          },
                          storeProperties,
                          storeId,
                          user,
                          billingConnected,
                          setStorePlan,
                          setupStripeConnection,
                          handleStripeBillingEmail,
                          handleBillingProvider,
                          saveOnboarding,
                        }}
                      />
                    )}
                    {currentStep === 5 && (
                      <Step5
                        {...{
                          formData: {
                            consent: formData.consent,
                          },
                          onHandleConsent,
                          consentError,
                        }}
                      />
                    )}
                  </>
                ) : (
                  <ThankYou />
                )}
              </div>

              {showThankYou ? (
                <div className="flex items-center justify-end mt-8 pt-6 border-t border-gray-200">
                  {/* Close Button */}
                  <button
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200`}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                  {/* Previous Button */}
                  <button
                    onClick={handlePrev}
                    disabled={currentStep === 1}
                    className={`flex items-center px-4 py-2 rounded-lg transition-colors duration-200 ${
                      currentStep === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <ArrowLeftIcon className="mr-2 w-6 h-6" />
                    Previous
                  </button>

                  <div className="text-center text-md">
                    <a
                      href="https://calendly.com/shipaid-support/expert-call"
                      target="_new"
                      className="flex gap-2 text-blue-500 mb-1"
                    >
                      <CalendarIcon width={25} />{' '}
                      <span className="text-underline">
                        Questions? We’re here to help, book a quick chat.
                      </span>
                    </a>
                  </div>

                  <div className="text-center">
                    {/* Next / Install / Complete Setup Button */}
                    {(() => {
                      const isLastStep = currentStep === totalSteps;
                      const isSecondStep = currentStep === 2;
                      const isOtherPlatform =
                        formData.platform === PLATFORM.OTHER;
                      const isInstallButtonDisabled =
                        !formData.shopifyUrl.includes('.myshopify.com') ||
                        (!formData.useCustomApp && !installLink) ||
                        (formData.useCustomApp &&
                          !formData.customAppInstallLink);
                      const canClick = canProceed();

                      if (
                        isLastStep ||
                        (isSecondStep && isOtherPlatform && !storeConnected)
                      ) {
                        return (
                          <button
                            onClick={() => {
                              if (canClick) {
                                if (!formData.consent) {
                                  setConsentError(true);
                                  return;
                                }
                                removeNewStoreProgressFlag();
                                saveOnboarding({ completed: true });
                                if (formData.completed) {
                                  setIsOpen(false);
                                } else {
                                  setShowThankYou(true);
                                  notifySetupGuideCompleted({
                                    variables: {
                                      input: {
                                        storeId,
                                      },
                                    },
                                  });
                                }
                              }
                            }}
                            disabled={!canClick}
                            className={`flex items-center px-6 py-2 rounded-lg transition-colors duration-200 ${
                              canClick
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            Complete Setup
                            <CheckIcon className="ml-2 w-8 h-8" />
                          </button>
                        );
                      }

                      if (isSecondStep && !storeConnected && !isOtherPlatform) {
                        return (
                          <button
                            onClick={install}
                            disabled={isInstallButtonDisabled}
                            className={`flex items-center px-6 py-2 rounded-lg 
                      transition-colors duration-200 text-white ${
                        isInstallButtonDisabled
                          ? '!disabled bg-gray-300'
                          : 'bg-[#2563eb] hover:bg-blue-700'
                      }`}
                          >
                            Install
                          </button>
                        );
                      }

                      return (
                        <button
                          onClick={handleNext}
                          disabled={!canClick}
                          className={`flex items-center px-6 py-2 rounded-lg transition-colors duration-200 ${
                            canClick
                              ? 'bg-[#2563eb] text-white hover:bg-blue-700'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          } ${
                            (storeConnected && currentStep === 2) ||
                            (billingConnected && currentStep === 4)
                              ? '!bg-green-600'
                              : ''
                          }`}
                        >
                          {currentStep > 1 ? 'Next' : 'Let’s Go'}
                          <ArrowRightIcon className="ml-2 w-6 h-6" />
                        </button>
                      );
                    })()}
                    <span className="text-sm">
                      Step {currentStep} of {totalSteps}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {
        <SelectStoreModal
          isOpen={isStoreModalOpen}
          onClose={() => setIsStoreModalOpen(false)}
        />
      }
    </>
  );
};

export default SetupGuideFlow;
